import { useEffect, useState } from 'react';
import { FiArrowUpRight, FiMapPin } from 'react-icons/fi';
import { motion } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import LeadForm from './components/LeadForm';
import PropertyGallery from './components/PropertyGallery';
import { FAQ, Highlights, Introduction, LocationSection, QuickFacts } from './components/PropertySections';
import SiteFooter from './components/SiteFooter';
import VideoTour from './components/VideoTour';
import { property, propertyAddress } from './config/property';
import { initializeTracking, trackEvent } from './lib/tracking';
import './hero-photo.css';
import './wordmark.css';

// Every CTA passes its intent here; the form selects it and the page scrolls to the form itself.
function scrollToForm(interest = 'In-person showing') {
  window.dispatchEvent(new CustomEvent('property-interest', { detail: interest }));
  const target = document.getElementById('inquiry-form') || document.getElementById('inquiry');
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  target?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
}

function requestShowing() {
  trackEvent('ScheduleShowingClick', { property_address: property.address });
  scrollToForm();
}

function requestDetails() {
  trackEvent('PropertyDetailsClick', { property_address: property.address });
  scrollToForm('Property question');
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-art" aria-hidden="true">
        <img
          className="hero-photo"
          src={property.images[0].src}
          alt=""
          width="800"
          height="600"
          fetchPriority="high"
          decoding="async"
        />
      </div>
      <div className="hero-copy">
        <span className="hero-kicker"><SafeIcon icon={FiMapPin} /> {property.city}, {property.state}</span>
        <h1>Modern One-Level Living <em>in Mount Holly</em></h1>
        <p className="hero-description">Built in 2023. One-level living. A cul-de-sac setting with a covered porch and fenced backyard.</p>
        <div className="hero-price">${property.price.toLocaleString()}</div>
        <div className="hero-address">{propertyAddress}</div><p className="hero-agent">Listed by Fanny Torres, Keller Williams Ballantyne Area</p>
        <div className="hero-specs"><span>3 beds</span><i /><span>2 baths</span><i /><span>1,644 sq. ft.</span><i /><span>Built 2023</span></div>
        <div className="hero-actions">
          <button className="button button-dark" onClick={requestShowing}>Request a Showing <SafeIcon icon={FiArrowUpRight} /></button>
          <button className="button button-accent" onClick={requestDetails}>Request Property Details <SafeIcon icon={FiArrowUpRight} /></button>
          <a className="button button-outline" href={`tel:${property.contactPhone}`}>Call Fanny <SafeIcon icon={FiArrowUpRight} /></a>
        </div>
      </div>
      <div className="hero-note"><span>01 / 01</span><span>Mount Holly · North Carolina</span></div>
    </section>
  );
}

function ConversionSections() {
  return (
    <>
      <section className="section inquiry-section" id="inquiry">
        <div className="inquiry-copy">
          <p className="eyebrow">Let's talk about the property</p>
          <h2>Interested in This Home?</h2>
          <p>Request a showing or ask a property question. Share the best way to reach you and what you’d like to know.</p>
          <div className="inquiry-address"><SafeIcon icon={FiMapPin} />{propertyAddress}</div>
        </div>
        <LeadForm />
      </section>
      <section className="secondary-cta">
        <div><p className="eyebrow">Your next step</p><h2>Want to See It in Person?</h2><p>Request a private showing and get answers to your questions about the property.</p></div>
        <button className="button button-light" onClick={() => scrollToForm()}>Request a Showing <SafeIcon icon={FiArrowUpRight} /></button>
      </section>
    </>
  );
}

function FinalCTA() {
  return (
    <section className="final-cta">
      <div className="final-art" aria-hidden="true">
        <img
          className="final-photo"
          src="https://media-manager-c.questera.ai/greta-media/93693806e52d557505bcda151baedfc511d2a8846f04584b1cb3f9d6077d8cb7ad67425a4311fb1bee70aa0e3387aa1d/images/aW1hZ2Uvd2VicA==/f6f4bcb1dd27c348095665f38a223065.webp"
          alt=""
          width="1344"
          height="1008"
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="final-copy">
        <p className="eyebrow">3649 Azalea Ridge Dr</p>
        <h2>Interested in this Mount Holly home?</h2>
        <p>Ask a question or request a private showing.</p>
        <div className="final-actions">
          <button className="button button-light" onClick={requestShowing}>Request a Showing</button>
          <button className="button button-glass" onClick={requestDetails}>Request Property Details</button>
        </div>
      </div>
    </section>
  );
}

function StickyMobileCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const inquiry = document.getElementById('inquiry');
    let formVisible = false;
    const update = () => setVisible(window.scrollY > 260 && !formVisible);
    const observer = new IntersectionObserver(([entry]) => {
      formVisible = entry.isIntersecting;
      update();
    }, { threshold: 0.08 });
    if (inquiry) observer.observe(inquiry);
    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', update);
    };
  }, []);
  return visible ? (
    <div className="mobile-sticky">
      <div className="sticky-actions">
        <button className="button button-accent" aria-label="Request Property Details" onClick={requestDetails}>Property Details</button>
        <button className="button button-dark" onClick={requestShowing}>Request a Showing</button>
      </div>
    </div>
  ) : null;
}

export default function App() {
  useEffect(() => {
    initializeTracking();
  }, []);

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="wordmark" href="#top" aria-label="Azalea Ridge home"><span className="wordmark-symbol" aria-hidden="true"><span className="wordmark-roof" /><span className="wordmark-door" /></span><span className="wordmark-name" aria-hidden="true">AZALEA<span>RIDGE</span></span></a>
        <a className="topbar-address" href="#top">{property.address}<span>{property.city}, {property.state}</span></a>
        <button className="topbar-cta" onClick={() => scrollToForm()}>Request a Showing <SafeIcon icon={FiArrowUpRight} /></button>
      </header>
      <main id="top">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: 'easeOut' }}>
          <Hero />
        </motion.div>
        <QuickFacts />
        <Introduction />
        <PropertyGallery images={property.images} />
        <Highlights />
        <VideoTour />
        <LocationSection />
        <ConversionSections />
        <FAQ />
        <FinalCTA />
      </main>
      <SiteFooter />
      <StickyMobileCTA />
    </div>
  );
}